import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Archive, X } from "@phosphor-icons/react";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import * as selectors from "selectors";
import useUpdateClientInsurance from "hooks/api/client/useSaveClientInsurance";
import useDisableClientInsurance from "hooks/api/client/useDisableClientInsurance";
import { useDispatch } from "react-redux";
import actions from "actions";

interface PrimaryInsuranceAsInactiveDialogProps {
  open: boolean;
  onClose: () => void;
  insurance: any;
  clientId: string;
}

export const PrimaryInsuranceAsInactiveDialog: React.FC<
  PrimaryInsuranceAsInactiveDialogProps
> = ({ open, onClose, insurance, clientId }) => {
  const userInsurance: any = useSelector(selectors.getUserInsurance);
  const { mutateAsync: updateInsuranceMutation, isMutationInsuranceLoading: isUpdating } =
    useUpdateClientInsurance();
  const { mutateAsync: disableInsuranceMutation, isMutationDisableInsuranceLoading: isDisabling } =
    useDisableClientInsurance();
  const dispatch = useDispatch();

  const [selectedOption, setSelectedOption] = useState("secondary-as-primary");

  const isLoading = isUpdating || isDisabling;

  const secondaryInsurance = useMemo(() => {
    return userInsurance.find((insurance: any) => !insurance.is_primary && insurance.active);
  }, [userInsurance]);

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  const handleConfirm = () => {
    if (selectedOption === "secondary-as-primary") {
      confirmSetPrimaryAsInactive(true);
    } else if (selectedOption === "do-nothing") {
      confirmSetPrimaryAsInactive(false);
    }
  };

  const confirmSetPrimaryAsInactive = async (promoteSecondaryToPrimary = false) => {
    if (insurance) {
      try {
        if (promoteSecondaryToPrimary) {
          const secondaryInsurance = userInsurance.find(
            (ins: any) => !ins.is_primary && ins.active
          );
          if (secondaryInsurance) {
            await updateInsuranceMutation({ id: secondaryInsurance.id, isPrimary: true });
          }
        }

        await disableInsuranceMutation({ id: insurance.id });
        dispatch(actions.getUserInsurance(parseInt(clientId)));
        onClose();
      } catch (error) {
        console.error("Error inactivating primary insurance:", error);
      }
    }
  };

  const handleClose = () => {
    setSelectedOption("");
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      aria-labelledby="insurance-inactive-title"
    >
      <DialogContent sx={{ zIndex: 2010 }}>
        <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
          Set insurance coverage as inactive
          <IconButton aria-label="close" onClick={handleClose} disabled={isLoading}>
            <X />
          </IconButton>
        </DialogTitle>

        <Box display="flex" flexDirection="column" sx={{ padding: "8px 0px 0px 0px" }} gap={5}>
          <Typography>
            How should the secondary insurance coverage be handled when removing primary coverage?
          </Typography>
          <FormControl>
            <RadioGroup value={selectedOption} onChange={handleOptionChange}>
              <FormControlLabel
                control={<Radio />}
                label="Set secondary insurance as the new primary"
                value="secondary-as-primary"
                disabled={!secondaryInsurance || isLoading}
              />
              <FormControlLabel
                control={<Radio />}
                label="Do nothing, leaving no primary insurance coverage"
                value="do-nothing"
                disabled={isLoading}
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          style={{ marginRight: "8px" }}
          variant="contained"
          color="secondary"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          startIcon={<Archive size={16} />}
          onClick={handleConfirm}
          disabled={!selectedOption}
          loading={isLoading}
        >
          Set as inactive
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
